import { useEffect, useRef } from 'react';
import type { Loop, Mode, Selection } from '../types';
import { computePeaks } from '../audio/peaks';
import type { Player } from '../audio/player';
import { clamp } from '../format';

const COL = {
  wave: 'rgba(236,238,246,0.82)',
  waveQ: 'rgba(61,220,132,0.82)',
  onset: '255,107,53',
  onsetQ: '61,220,132',
  grid: 'rgba(121,134,203,',
  loop: '176,124,255',
  sel: '255,107,53',
};

interface Props {
  buffer: AudioBuffer | null;
  view: Selection;
  selection: Selection | null;
  loops: Loop[];
  activeLoop: string | null;
  onsets: number[];
  bpm: number | null;
  division: number;
  gridOffset: number;
  quantized: boolean;
  mode: Mode;
  player: Player;
  interactive: boolean;
  onSelect: (sel: Selection | null) => void;
  onSeek: (t: number) => void;
  onView: (v: Selection) => void;
}

export default function Waveform(p: Props) {
  const staticRef = useRef<HTMLCanvasElement | null>(null);
  const headRef = useRef<HTMLCanvasElement | null>(null);
  const hitRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x0: number; t0: number; moved: boolean } | null>(null);
  const pointers = useRef<Map<number, number>>(new Map()); // pointeurs actifs (clientX) pour le pincement
  const pinch = useRef<{ t1: number; t2: number } | null>(null);
  const live = useRef(p);
  live.current = p;

  /* ------------------------------------------------ couches statiques */
  useEffect(() => {
    const cv = staticRef.current;
    if (!cv) return;

    const draw = () => {
      const { buffer, view, selection, loops, activeLoop, onsets, bpm, division, gridOffset, quantized } =
        live.current;
      const dpr = window.devicePixelRatio || 1;
      const W = Math.max(1, Math.round(cv.clientWidth * dpr));
      const H = Math.max(1, Math.round(cv.clientHeight * dpr));
      if (cv.width !== W || cv.height !== H) {
        cv.width = W;
        cv.height = H;
      }
      const g = cv.getContext('2d');
      if (!g) return;
      g.clearRect(0, 0, W, H);
      if (!buffer) return;

      const a = view.start;
      const b = view.end > a ? view.end : buffer.duration;
      const span = b - a;
      if (span <= 0) return;
      const px = (t: number) => ((t - a) / span) * W;
      const mid = H / 2;

      // boucles
      for (const l of loops) {
        const on = l.id === activeLoop;
        const x0 = px(l.start);
        const x1 = px(l.end);
        if (x1 < 0 || x0 > W) continue;
        g.fillStyle = `rgba(${COL.loop},${on ? 0.14 : 0.06})`;
        g.fillRect(x0, 0, x1 - x0, H);
        g.fillStyle = `rgba(${COL.loop},${on ? 1 : 0.5})`;
        const w = on ? 2 : 1;
        g.fillRect(x0, 0, w, H);
        g.fillRect(x1 - w, 0, w, H);
      }

      // sélection
      if (selection && selection.end > selection.start) {
        const x0 = px(selection.start);
        const x1 = px(selection.end);
        g.fillStyle = `rgba(${COL.sel},0.09)`;
        g.fillRect(x0, 0, x1 - x0, H);
        g.fillStyle = `rgba(${COL.sel},0.5)`;
        g.fillRect(x0, 0, 2, H);
        g.fillRect(x1 - 2, 0, 2, H);
      }

      // grille
      if (bpm && bpm > 0) {
        const step = (60 / bpm) * division;
        if (step * (W / span) > 3) {
          const perBeat = Math.max(1, Math.round(1 / division));
          let k = Math.floor((a - gridOffset) / step);
          g.lineWidth = 1;
          for (let t = gridOffset + k * step; t <= b; t += step, k++) {
            if (t < 0) continue;
            const x = Math.round(px(t)) + 0.5;
            const strong = ((k % perBeat) + perBeat) % perBeat === 0;
            g.strokeStyle = `${COL.grid}${strong ? 0.5 : 0.22})`;
            g.beginPath();
            g.moveTo(x, 0);
            g.lineTo(x, H);
            g.stroke();
          }
        }
      }

      // forme d'onde
      const peaks = computePeaks(buffer, Math.min(W, 4096), a, b);
      const scale = W / peaks.columns;
      g.fillStyle = quantized ? COL.waveQ : COL.wave;
      for (let i = 0; i < peaks.columns; i++) {
        const hi = peaks.max[i] * mid * 0.94;
        const lo = peaks.min[i] * mid * 0.94;
        const x = i * scale;
        const h = Math.max(1, hi - lo);
        g.fillRect(x, mid - hi, Math.max(1, scale), h);
      }

      // transitoires
      const col = quantized ? COL.onsetQ : COL.onset;
      for (const t of onsets) {
        if (t < a || t > b) continue;
        const x = Math.round(px(t));
        g.fillStyle = `rgba(${col},0.38)`;
        g.fillRect(x, 0, 2, H);
        g.fillStyle = `rgba(${col},1)`;
        g.fillRect(x, 0, 2, H * 0.1);
      }
    };

    draw();

    const ro = new ResizeObserver(draw);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [
    p.buffer,
    p.view.start,
    p.view.end,
    p.selection?.start,
    p.selection?.end,
    p.loops,
    p.activeLoop,
    p.onsets,
    p.bpm,
    p.division,
    p.gridOffset,
    p.quantized,
  ]);

  /* ------------------------------------------------ tête de lecture */
  useEffect(() => {
    const cv = headRef.current;
    if (!cv) return;
    let raf = 0;

    const frame = () => {
      const { buffer, view, player } = live.current;
      const dpr = window.devicePixelRatio || 1;
      const W = Math.max(1, Math.round(cv.clientWidth * dpr));
      const H = Math.max(1, Math.round(cv.clientHeight * dpr));
      if (cv.width !== W || cv.height !== H) {
        cv.width = W;
        cv.height = H;
      }
      const g = cv.getContext('2d');
      if (g) {
        g.clearRect(0, 0, W, H);
        if (buffer) {
          const a = view.start;
          const b = view.end > a ? view.end : buffer.duration;
          const t = player.time;
          if (t >= a && t <= b) {
            const x = Math.round(((t - a) / (b - a)) * W) + 0.5;
            g.strokeStyle = 'rgba(255,255,255,0.9)';
            g.lineWidth = 2;
            g.beginPath();
            g.moveTo(x, 0);
            g.lineTo(x, H);
            g.stroke();
          }
        }
      }
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* ------------------------------------------------ pointeur, zoom, pincement */
  const viewBounds = (): [number, number] => {
    const { buffer, view } = live.current;
    const a = view.start;
    const b = view.end > a ? view.end : buffer?.duration ?? 0;
    return [a, b];
  };

  const fracAt = (clientX: number): number => {
    const el = hitRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp((clientX - r.left) / Math.max(1, r.width), 0, 1);
  };

  const timeAt = (clientX: number): number => {
    const [a, b] = viewBounds();
    return a + fracAt(clientX) * (b - a);
  };

  /** Applique une fenêtre de vue bornée (min 50 ms, dans [0, durée]). */
  const applyView = (a: number, b: number) => {
    const dur = live.current.buffer?.duration ?? 0;
    if (dur <= 0) return;
    const span = clamp(b - a, 0.05, dur);
    const start = clamp(a, 0, dur - span);
    live.current.onView({ start, end: start + span });
  };

  // Molette PC : zoom centré sous le curseur (listener natif non passif pour preventDefault).
  useEffect(() => {
    const el = hitRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const { buffer, interactive } = live.current;
      if (!interactive || !buffer) return;
      e.preventDefault();
      const [a, b] = viewBounds();
      const span = b - a;
      const f = fracAt(e.clientX);
      const center = a + f * span;
      const factor = e.deltaY < 0 ? 1 / 1.2 : 1.2;
      const ns = clamp(span * factor, 0.05, buffer.duration);
      applyView(center - f * ns, center - f * ns + ns);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onDown = (e: React.PointerEvent) => {
    if (!p.interactive || !p.buffer) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, e.clientX);
    if (pointers.current.size === 2) {
      // deux doigts → pincement ; on abandonne une éventuelle sélection en cours
      drag.current = null;
      const xs = [...pointers.current.values()];
      const [a, b] = viewBounds();
      const span = b - a;
      const f1 = fracAt(Math.min(xs[0], xs[1]));
      const f2 = fracAt(Math.max(xs[0], xs[1]));
      pinch.current = { t1: a + f1 * span, t2: a + f2 * span };
      return;
    }
    drag.current = { x0: e.clientX, t0: timeAt(e.clientX), moved: false };
  };

  const onMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, e.clientX);
    if (pinch.current && pointers.current.size >= 2) {
      const xs = [...pointers.current.values()].sort((a, b) => a - b);
      const f1 = fracAt(xs[0]);
      const f2 = fracAt(xs[1]);
      if (f2 - f1 > 0.02) {
        const span = (pinch.current.t2 - pinch.current.t1) / (f2 - f1);
        applyView(pinch.current.t1 - f1 * span, pinch.current.t1 - f1 * span + span);
      }
      return;
    }
    const d = drag.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.x0) > 4) d.moved = true;
    if (!d.moved) return;
    const t = timeAt(e.clientX);
    p.onSelect({ start: Math.min(d.t0, t), end: Math.max(d.t0, t) });
  };

  const onUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) {
      p.onSelect(null);
      p.onSeek(timeAt(e.clientX));
    }
  };

  return (
    <>
      <canvas ref={staticRef} aria-hidden="true" />
      <canvas ref={headRef} aria-hidden="true" />
      {p.interactive && (
        <div
          ref={hitRef}
          className="wave-hit"
          role="slider"
          aria-label="Forme d'onde — glisse pour sélectionner un passage"
          aria-valuemin={0}
          aria-valuemax={Math.round(p.buffer?.duration ?? 0)}
          aria-valuenow={Math.round(p.player.time)}
          tabIndex={0}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        />
      )}
    </>
  );
}
